import { addDays } from 'date-fns';
import { find, includes, isEmpty, prop, propEq } from 'ramda';
import { isNilOrEmpty } from 'ramda-extension';

import { MPU_CREATE_BACKOFFICE_TASK, MPU_CREATE_MOBILE_SCANNING_TASK } from 'features/common/acl/constants';
import { radioOptionsIds } from 'features/other/resolverSettings/constants';
import { toIsoDateFormat } from 'utils/apiDateTime';
import { cleanse } from 'utils/object';

import { taskTypeBackoffice, taskTypeContactClient, taskTypeMobileScan, taskTypeSellerRequest } from './constants';
import {
	BACKOFFICE_FORM_NAME,
	CONTACT_CLIENT_FORM_NAME,
	MOBILE_SCAN_FORM_NAME,
	SELLER_REQUEST_FORM_NAME,
} from './form';

export const generateSolver = (solverFormData) => {
	const solver = {};
	switch (solverFormData.radio) {
		case radioOptionsIds.superior:
			solver.solverLogin = solverFormData.superior;
			break;
		case radioOptionsIds.subordinate:
			solver.solverLogin = solverFormData.subordinate;
			break;
		case radioOptionsIds.agency:
			solver.solverLogin = solverFormData.agency;
			break;
		case radioOptionsIds.region:
			solver.solverLogin = solverFormData.region;
			break;
		case radioOptionsIds.users:
			solver.solverLogin = solverFormData.login;
			solver.solverSellerIn = solverFormData.solverSellerIn;
			break;
		case radioOptionsIds.marketPlace:
			// no changes - we do not fill solver in case of marketPlace radio
			break;
	}

	return solver;
};

export const bodyFactory = ({ formData, selectedClientFromSearch, selectedPolicyFromSearch }) => {
	const solver = generateSolver(formData);
	const comments = [formData?.note, formData?.additionalContactReason].filter((value) => !isNilOrEmpty(value));

	return cleanse({
		taskTypeId: Number(formData?.taskType),
		taskSubTypeId: Number(formData?.taskSubTypeId),
		client: {
			businessName: formData?.businessName,
			businessIn: formData?.businessIn,
			city: formData?.municipality,
			firstname: formData?.firstName,
			surname: formData?.surname,
			street: formData?.street,
			streetNo: formData?.streetNo,
			zipCode: formData?.zipCode,
			personIn: formData?.personIn,
			ccdId: selectedClientFromSearch?.id,
			...(!includes(formData?.taskType, [taskTypeBackoffice, taskTypeMobileScan]) && {
				phoneNo: formData?.phoneNumber,
				email: formData?.email,
			}),
		},
		comments,
		metadata: {
			common: {
				title: formData?.title,
				additionalContactReason: formData?.additionalContactReason,
				...(formData?.taskType === taskTypeSellerRequest && {
					birthDate: toIsoDateFormat(formData?.birthDate),
				}),
				...(formData?.taskType === taskTypeBackoffice && { segment: formData?.backOfficeSegment }),
			},
		},
		policyNo: formData?.policyNumber ? Number(formData?.policyNumber) : null,
		proposalNo: formData?.proposalNumber,
		deadlineDate: toIsoDateFormat(formData?.fulfillDate),
		notificationDate: toIsoDateFormat(formData?.warningDate),
		claimNo: formData?.claimNoCP,
		policySystemId: selectedPolicyFromSearch?.systemId,
		...solver,
		...(!includes(formData?.taskType, [
			taskTypeBackoffice,
			taskTypeMobileScan,
			taskTypeSellerRequest,
			taskTypeContactClient,
		]) && {
			isForMarketplaceCompetition: isEmpty(solver), // if empty, than the solver was radioOption 'toTheMarketPlace'
		}),
	});
};

export const calculateDefaultDates = (taskTypeId, codelist) => {
	const findElem = find(propEq(taskTypeId, 'id'), codelist);
	const deadlineDays = prop('deadlineDays', findElem);
	const notificationDays = prop('notificationDays', findElem);
	const fulfillDate = addDays(new Date(), deadlineDays);
	const warningDate = addDays(new Date(), deadlineDays - notificationDays);
	return { fulfillDate, warningDate };
};

export const getCorrectTaskTypesByPageType = (pageType) => {
	switch (pageType) {
		case SELLER_REQUEST_FORM_NAME:
			return [Number(taskTypeSellerRequest)];
		case MOBILE_SCAN_FORM_NAME:
			return [Number(taskTypeMobileScan)];
		case BACKOFFICE_FORM_NAME:
			return [Number(taskTypeBackoffice)];
		case CONTACT_CLIENT_FORM_NAME:
			return [Number(taskTypeContactClient)];
	}
};

export const getCorrectDefaultTaskTypeValue = (pageType) => {
	switch (pageType) {
		case SELLER_REQUEST_FORM_NAME:
			return taskTypeSellerRequest;
		case MOBILE_SCAN_FORM_NAME:
			return taskTypeMobileScan;
		case BACKOFFICE_FORM_NAME:
			return taskTypeBackoffice;
		case CONTACT_CLIENT_FORM_NAME:
			return taskTypeContactClient;
	}
};

export const getCorrectAclPermissionByPageType = (pageType) => {
	switch (pageType) {
		case MOBILE_SCAN_FORM_NAME:
			return MPU_CREATE_MOBILE_SCANNING_TASK;
		case BACKOFFICE_FORM_NAME:
			return MPU_CREATE_BACKOFFICE_TASK;
		default:
			return null;
	}
};

export const findBackOfficeSegmentValue = (systemId, backOfficeSegmentCodelist) => {
	const filteredCodelist = backOfficeSegmentCodelist.filter((value) => value.policySrcId);
	const parsedCodelist = filteredCodelist.map(({ policySrcId, ...rest }) => {
		// ŽP option has two ids in string - 1,6
		const parsedPolicySrcId = policySrcId.split(',');
		return {
			...rest,
			policySrcId: parsedPolicySrcId,
		};
	});

	const autoFillValue = parsedCodelist.find(({ policySrcId }) => includes(systemId.toString(), policySrcId));
	return !isNilOrEmpty(autoFillValue) ? autoFillValue.id : null;
};
